import { lazy } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  Navigate,
} from 'react-router-dom';
import { ProtectedRoute } from './protected-route';
import { RoleGuard } from '@features/auth';
import { SECTION_CRUMB } from '@shared/ui/breadcrumbs';
import { EducatorLoginRedirect, RootRedirect, SuspenseLayout } from './route-shells';
import { RouteErrorPage } from './route-error-page';
const AppLayout = lazy(() => import('@app/layouts').then((m) => ({ default: m.AppLayout })));
const EducatorMobileShell = lazy(() =>
  import('@app/layouts/mobile').then((m) => ({ default: m.EducatorMobileShell }))
);

// Lazy-loaded page components
const LoginPage = lazy(() => import('@pages/login').then((m) => ({ default: m.LoginPage })));
const SettingsPage = lazy(() =>
  import('@pages/settings').then((m) => ({ default: m.SettingsPage }))
);
const CareersPage = lazy(() => import('@pages/careers').then((m) => ({ default: m.CareersPage })));
const CareersLayout = lazy(() =>
  import('@pages/careers').then((m) => ({ default: m.CareersLayout }))
);
const JobEditorPage = lazy(() =>
  import('@pages/job-editor').then((m) => ({ default: m.JobEditorPage }))
);
const SchoolsPage = lazy(() => import('@pages/schools').then((m) => ({ default: m.SchoolsPage })));
const OffersPage = lazy(() => import('@pages/offers').then((m) => ({ default: m.OffersPage })));
const OfferPage = lazy(() => import('@pages/offers').then((m) => ({ default: m.OfferPage })));
const MapPage = lazy(() => import('@pages/map').then((m) => ({ default: m.MapPage })));
const CourseDetailPage = lazy(() =>
  import('@pages/course-detail').then((m) => ({ default: m.CourseDetailPage }))
);
const CoursesPage = lazy(() => import('@pages/courses').then((m) => ({ default: m.CoursesPage })));
const SubstitutionsPage = lazy(() =>
  import('@pages/substitutions').then((m) => ({ default: m.SubstitutionsPage }))
);
const TodoPage = lazy(() => import('@pages/todo').then((m) => ({ default: m.TodoPage })));
const CourseAnalyticsPage = lazy(() =>
  import('@pages/course-analytics').then((m) => ({ default: m.CourseAnalyticsPage }))
);
const LehrkraeftePage = lazy(() =>
  import('@pages/lehrkraefte').then((m) => ({ default: m.LehrkraeftePage }))
);
const EducatorDetailPage = lazy(() =>
  import('@pages/lehrkraefte').then((m) => ({ default: m.EducatorDetailPage }))
);
const LehrkraefteFeedbackPage = lazy(() =>
  import('@pages/lehrkraefte').then((m) => ({ default: m.FeedbackPage }))
);
const SchoolDetailPage = lazy(() =>
  import('@pages/school-detail').then((m) => ({ default: m.SchoolDetailPage }))
);
const InvitationPage = lazy(() =>
  import('@pages/invitation').then((m) => ({ default: m.InvitationPage }))
);
const EducatorProfilePage = lazy(() =>
  import('@pages/educator/profile').then((m) => ({ default: m.EducatorProfilePage }))
);
const EducatorAppointmentPage = lazy(() =>
  import('@pages/educator/appointment').then((m) => ({ default: m.EducatorAppointmentPage }))
);
const EducatorSubstitutionDetailPage = lazy(() =>
  import('@pages/educator/substitution-detail').then((m) => ({
    default: m.EducatorSubstitutionDetailPage,
  }))
);
const EducatorCourseDetailPage = lazy(() =>
  import('@pages/educator/course-detail').then((m) => ({ default: m.EducatorCourseDetailPage }))
);
const EducatorBoardPage = lazy(() =>
  import('@pages/educator/board').then((m) => ({ default: m.EducatorBoardPage }))
);
const EducatorCoursesPage = lazy(() =>
  import('@pages/educator/courses').then((m) => ({ default: m.EducatorCoursesPage }))
);
const EducatorAbsencePage = lazy(() =>
  import('@pages/educator/absence').then((m) => ({ default: m.EducatorAbsencePage }))
);
const EducatorFeedbackPage = lazy(() =>
  import('@pages/educator/feedback').then((m) => ({ default: m.EducatorFeedbackPage }))
);
const EducatorMyFeedbackPage = lazy(() =>
  import('@pages/educator/feedback').then((m) => ({ default: m.EducatorMyFeedbackPage }))
);
const EducatorTutorialPage = lazy(() =>
  import('@pages/educator/tutorial').then((m) => ({ default: m.EducatorTutorialPage }))
);

/**
 * App Routes Configuration
 *
 * Defines all application routes with:
 * - Public routes (login)
 * - Protected routes (require authentication)
 * - Role-based routes (admin only)
 * - Lazy loading for optimal performance
 *
 * Data Router (statt `<BrowserRouter>`), weil `useBlocker` ausschliesslich
 * unter einem Data Router laeuft — er ist die einzige Moeglichkeit, beim
 * Wegnavigieren ungespeicherte Eingaben zu retten. Bewusst **ohne** `loader`:
 * die Datenschicht haengt durchgaengig an `onSnapshot`, ein Loader-Schnappschuss
 * wuerde Millisekunden spaeter vom Abo ueberschrieben.
 *
 * Seit dem 08.10.2026 (Spec „Mails und Feedback" s6, D13) traegt die aeusserste
 * Route ein `errorElement`: Jeder Absturz einer Seite und jede unbekannte
 * Adresse landet auf `RouteErrorPage` („Da ist etwas schiefgegangen") statt
 * auf der englischen Entwicklerseite von react-router.
 *
 * @example
 * ```tsx
 * import { router } from '@app/router';
 *
 * function App() {
 *   return (
 *     <AppProviders>
 *       <RouterProvider router={router} />
 *     </AppProviders>
 *   );
 * }
 * ```
 */
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<SuspenseLayout />} errorElement={<RouteErrorPage />}>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/educator" element={<EducatorLoginRedirect />} />
      <Route path="/invitation" element={<InvitationPage />} />

      {/* Microsoft OAuth callback (protected but outside layout) */}
      <Route element={<ProtectedRoute />}></Route>

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<RootRedirect />} />

        {/* Legacy routes: redirect to dashboard where wizards are now embedded */}

        {/* ─── EDUCATOR Routes → MobileLayout with Educator Tabs ─── */}
        <Route
          element={
            <RoleGuard allowedRoles={['educator']}>
              <EducatorMobileShell />
            </RoleGuard>
          }
        >
          {/* Kein eigener Startbildschirm: Er zeigte nur MyWeek ein zweites
                Mal — dieselbe Woche, die im Reiter "Kurse" unter Kalender
                steht. Der Umzug war beim Bau schon vorgesehen. */}
          <Route path="/e" element={<Navigate to="/e/courses" replace />} />
          <Route path="/e/profile" element={<EducatorProfilePage />} />
          <Route path="/e/board" element={<EducatorBoardPage />} />
          {/* Drei Routen, ein Reiter: die Segmente von „Kurse". */}
          <Route path="/e/courses" element={<EducatorCoursesPage />} />
          {/* Ein Kurs, zwei Pfade, eine Seite. Die untere Leiste hebt den
              Reiter ueber `startsWith` hervor: Unter einem gemeinsamen
              `/e/course/:id` leuchtete keiner, unter `/e/courses/:id`
              spraenge man vom Brett aus auf „Kurse". So bleibt der Reiter
              stehen, aus dem man kam — und der Rueckweg stimmt. */}
          <Route path="/e/board/:courseId" element={<EducatorCourseDetailPage />} />
          <Route path="/e/courses/:courseId" element={<EducatorCourseDetailPage />} />
          {/* `vertretung` als festes Segment haelt die drei Pfade
              auseinander: react-router rankt statische Segmente hoeher als
              dynamische, `/e/board/vertretung/x1` kann also nie als
              Kurs-Id durchgehen. */}
          <Route
            path="/e/board/vertretung/:exceptionId"
            element={<EducatorSubstitutionDetailPage />}
          />
          <Route
            path="/e/substitutions/:exceptionId"
            element={<EducatorSubstitutionDetailPage />}
          />
          {/* Kein eigenes Segment mehr: Die uebernommenen Vertretungen
              stehen seit dem 07.09.2026 mit unter „Meine Kurse" — ein
              Reiter fuer meist null Eintraege verriet erst beim Antippen,
              dass er leer ist. Der Pfad leitet um, damit Verweise aus
              alten Nachrichten nicht ins Leere laufen. */}
          <Route path="/e/substitutions" element={<Navigate to="/e/courses" replace />} />
          <Route path="/e/calendar" element={<EducatorCoursesPage />} />
          {/* Ein einzelner Termin. Man fragt fuer einen Termin eine
              Vertretung an, nicht fuer einen Kurs — deshalb steht das
              Datum im Pfad. */}
          <Route path="/e/calendar/:courseId/:date" element={<EducatorAppointmentPage />} />
          {/* "Ich falle aus" (s1): ein Zeitraum für alle eigenen Kurse,
              statt einen Termin nach dem anderen zu melden. */}
          <Route path="/e/ausfall" element={<EducatorAbsencePage />} />
          {/* Feedback (s6): Formular und „Meine Meldungen", beide aus dem
              Profil erreichbar und Teil des Reiters „Profil". */}
          <Route path="/e/feedback" element={<EducatorFeedbackPage />} />
          <Route path="/e/meldungen" element={<EducatorMyFeedbackPage />} />
          {/* Einführung für Lehrkräfte (s1): Beispielseite mit Erklärkarte,
              in s1 nur aus dem Profil erreichbar und Teil des Reiters
              „Profil". */}
          <Route path="/e/einfuehrung" element={<EducatorTutorialPage />} />
        </Route>

        {/* ─── ADMIN/EMPLOYEE Routes → AppLayout (Sidebar) ─── */}
        <Route
          element={
            <RoleGuard allowedRoles={['admin', 'employee']} redirectTo="/e">
              <AppLayout />
            </RoleGuard>
          }
        >
          {/* Other modules */}

          {/* Karriere: Liste, Anlegen und Bearbeiten unter einer Bereichs-Route.
              Der SectionLabelsProvider sitzt im Layout, damit ihn auch der
              Editor sieht. "new" steht vor ":jobId" — react-router 7 rankt
              statische Segmente ohnehin hoeher, aber so liest es sich wie der
              Rest der Datei. */}
          <Route path="/careers" element={<CareersLayout />} handle={SECTION_CRUMB}>
            <Route index element={<CareersPage />} />
            <Route path="new" element={<JobEditorPage />} />
            <Route path=":jobId" element={<JobEditorPage />} />
          </Route>
          {/* ─── Bereiche mit Liste und Detail ───────────────────────────
              Liste und Detail hängen ineinander statt nebeneinander. Das ist
              nicht Geschmack: `useMatches()` liefert die Kette der
              *getroffenen* Routen, und daraus baut `<Breadcrumbs>` den Weg
              („Kurse / Mathematik 8b"). Flache Geschwister ergeben eine Kette
              der Länge eins — und einen Rückweg, den jede Seite selbst raten
              müsste. Genau das war der Fehler bis zum 10.09.2026: Wer aus
              einem Kurs auf eine Lehrkraft sprang, landete beim Zurück in der
              Lehrkräfte-Liste.

              Die Bereichs-Route trägt `element={<Outlet />}` und **kein**
              eigenes Element: Sie erzeugt damit kein DOM, und die Höhenkette
              vom App-Layout zur Seite bleibt unberührt — dieselbe Überlegung
              wie in `CareersLayout`.

              `handle={SECTION_CRUMB}` ist alles, was ein Bereich dafür
              braucht; seine Beschriftung holt es sich aus
              `shared/config/navigation.ts`, damit Navigation und Brotkrumen
              nicht auseinanderlaufen. Die Detailrouten tragen **nichts** —
              ihren Namen trägt die Überschrift der Seite. */}
          <Route path="/lehrkraefte" element={<Outlet />} handle={SECTION_CRUMB}>
            <Route index element={<LehrkraeftePage />} />
            {/* Feedback der Lehrkräfte (Spec Mails & Feedback s6). Vor ":id",
                sonst frisst der Platzhalter den festen Pfad. */}
            <Route path="feedback" element={<LehrkraefteFeedbackPage />} />
            <Route path=":id" element={<EducatorDetailPage />} />
          </Route>
          <Route path="/schools" element={<Outlet />} handle={SECTION_CRUMB}>
            <Route index element={<SchoolsPage />} />
            {/* Vor ":schoolId", sonst frisst der Platzhalter den festen Pfad.
                Seit dem 11.09.2026 nur noch Weiterleitung: Die Zuordnung ist
                ein Abschnitt der Einstellungen, alte Links landen dort. */}
            <Route
              path="lobw-zuordnung"
              element={<Navigate to="/settings?section=lobw-zuordnung" replace />}
            />
            {/* Alle Angebote, auch an Interessenten-Schulen (Spec
                Förderbudget D11). Ebenfalls vor ":schoolId".

                Seit s3 ein eigener Bereich (Spec Angebote D11): Liste,
                Anlegen und Bearbeiten, dieselbe Form wie die Karriere. Die
                Krume „Angebote" trägt der Bereich, das einzelne Angebot
                nicht — seinen Namen trägt die Überschrift. "neu" vor
                ":offerId", wie "new" vor ":jobId". */}
            <Route path="angebote" element={<Outlet />} handle={SECTION_CRUMB}>
              <Route index element={<OffersPage />} />
              <Route path="neu" element={<OfferPage />} />
              <Route path=":offerId" element={<OfferPage />} />
            </Route>
            <Route path=":schoolId" element={<SchoolDetailPage />} />
          </Route>
          <Route path="/courses" element={<Outlet />} handle={SECTION_CRUMB}>
            <Route index element={<CoursesPage />} />
            {/* Vor ":courseId", sonst frisst der Platzhalter den festen Pfad —
                derselbe Fall wie bei ":schoolId" oben. */}
            <Route path="analytics" element={<CourseAnalyticsPage />} />
            <Route path=":courseId" element={<CourseDetailPage />} />
          </Route>
          <Route path="/karte" element={<MapPage />} />
          <Route path="/todo" element={<TodoPage />} />
          <Route path="/substitutions" element={<SubstitutionsPage />} />

          {/* Settings: the office — admin and employee */}
          <Route
            path="/settings"
            element={
              <RoleGuard allowedRoles={['admin', 'employee']}>
                <SettingsPage />
              </RoleGuard>
            }
          />
        </Route>
      </Route>
    </Route>
  )
);
